import type { TrainingSessionListView } from "@/api";
import type { CheckpointTag } from "@/modules/training/public";
import { notifyWindowCapReached, windowStack } from "@/app/windowStack";
import { sessionDisplayName } from "./trainingSessionFormat";

// 세션 상세 창의 탭. 콘텐츠가 탭 목록을 그리고, 여는 쪽은 focus 로 그중 하나를 지정한다.
export type TrainingSessionDetailTab = "logs" | "config" | "analysis" | "checkpoints" | "datasets";

// 창을 열 때 먼저 표시할 위치. 없으면 로그 탭이다. checkpoint 는 체크포인트 탭에서 강조·스크롤할
// 행(태그 단위·번호)이다. Policy 이름 링크가 그 정책의 체크포인트를 가리킬 때 보낸다. 호출마다 새
// 객체라서, 이미 열린 창에 같은 위치를 다시 요청해도 콘텐츠가 그 위치로 돌아온다.
export type TrainingSessionWindowFocus = {
  tab: TrainingSessionDetailTab;
  checkpoint?: CheckpointTag | null;
};

// trainingSession kind 엔트리의 params 계약. 콘텐츠(TrainingSessionWindowContent)가 읽고, 세션
// 테이블이 open 할 때 채운다. 한 곳에서만 정의해 정의가 서로 달라지지 않게 한다. owned 는 제출 직후
// 여는 창의 owner 강제 플래그다. 제출 응답에는 선점자(registry_user)가 없어 행 비교를 할 수 없다.
// 그러나 제출은 본인이 선점한 workspace 에만 가능하므로 owner 가 확정된다.
export type TrainingSessionWindowParams = {
  session: TrainingSessionListView;
  owned?: boolean;
  focus?: TrainingSessionWindowFocus;
};

export type OpenTrainingSessionWindowOptions = {
  owned?: boolean;
  focus?: TrainingSessionWindowFocus;
};

// 학습 세션 상세를 전체 화면 창 스택으로 연다(세션 테이블 행 클릭·제출 직후·Policy 이름 링크).
// 같은 세션 창이 이미 열려 있으면 그 창을 복원한다. cap(5)을 넘으면 열지 않고 toast 로 안내한다.
export function openTrainingSessionWindow(
  session: TrainingSessionListView,
  options: OpenTrainingSessionWindowOptions = {},
): void {
  const params = {
    session,
    owned: options.owned,
    focus: options.focus,
  } satisfies TrainingSessionWindowParams;
  const id = windowStack.open({
    kind: "trainingSession",
    key: session.id,
    title: sessionDisplayName(session),
    params,
  });
  if (id === null) {
    notifyWindowCapReached();
    return;
  }
  // open 은 기존 엔트리의 params 를 갱신하지 않는다. 복원된 창(params 참조를 이 함수가 만들지
  // 않은 경우)에 focus 요청이 있으면 그 위치만 병합한다. 링크가 전달하는 session 은 status 를 모르는
  // 최소 참조라서 기존 session·owned 를 덮으면 안 된다. focus 없는 호출은 보던 탭을 그대로 둔다.
  const entry = windowStack.getSnapshot().entries.find((candidate) => candidate.id === id);
  if (options.focus !== undefined && entry !== undefined && entry.params !== params) {
    const existing = entry.params as TrainingSessionWindowParams;
    windowStack.update(id, { params: { ...existing, focus: options.focus } });
  }
}
